/* What the funnel keeps in the browser (no personal data beyond what the visitor typed on
   S1/S2): the current check's enquiry id, the trip draft before the enquiry exists, the
   navigation context (first pass or returning, and what a link was for), and the details of
   an expired link for S14-expired. Per tab (sessionStorage), except `enquiry_id` in
   localStorage, which the homepage banner reads (contract: the only thing localStorage holds). */
import { useSyncExternalStore } from 'react';
import type { Airport, Attribution, Entry, ExpiredLink, LinkTarget, Visit } from '../api/contract';

type StorageKind = 'session' | 'local';

function storage(kind: StorageKind): Storage | null {
  try {
    return kind === 'session' ? globalThis.sessionStorage : globalThis.localStorage;
  } catch {
    return null;
  }
}

/** A value kept in web storage, observable from React (`useSyncExternalStore`). */
export interface Slot<T> {
  get(): T;
  set(value: T): void;
  clear(): void;
  subscribe(listener: () => void): () => void;
}

function slot<T>(key: string, kind: StorageKind, initial: T): Slot<T> {
  const listeners = new Set<() => void>();
  let cacheRaw: string | null | undefined;
  let cacheValue: T = initial;
  const read = (): T => {
    const raw = storage(kind)?.getItem(key) ?? null;
    if (raw !== cacheRaw) {
      cacheRaw = raw;
      try {
        cacheValue = raw === null ? initial : (JSON.parse(raw) as T);
      } catch {
        cacheValue = initial;
      }
    }
    return cacheValue;
  };
  const emit = () => listeners.forEach((l) => l());
  return {
    get: read,
    set(value) {
      try {
        storage(kind)?.setItem(key, JSON.stringify(value));
      } catch {
        /* private mode: the value just isn't kept */
      }
      emit();
    },
    clear() {
      storage(kind)?.removeItem(key);
      emit();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export function useSlot<T>(s: Slot<T>): T {
  return useSyncExternalStore(s.subscribe, s.get, s.get);
}

/* ── The current check ───────────────────────────────────── */

/** This tab's enquiry for the eligibility stage (S1–S6 have no id in the URL). */
export const currentEnquiry = slot<string | null>('db_enquiry', 'session', null);
/** The homepage banner's key (contract: `localStorage.enquiry_id`). */
export const bannerEnquiry = slot<string | null>('enquiry_id', 'local', null);

/** After `createEnquiry` / `createEnquiryFromScan`. */
export function rememberEnquiry(id: string) {
  currentEnquiry.set(id);
  bannerEnquiry.set(id);
}

/* ── S1/S2 before the enquiry exists ─────────────────────── */

/**
 * The trip as typed on S1/S2 until S3 creates the enquiry (the enquiry is created at the
 * first flight search, with the trip). After that, the enquiry's `trip` is the truth.
 */
export interface TripDraft {
  /** From the homepage's "What went wrong?" (`?type=`), null when unknown. */
  entry: Exclude<Entry, 'scan'> | null;
  origin: Airport | null;
  destination: Airport | null;
  connections: Airport[];
  date: string | null;
  /** utm_* and AffiliateWP, captured on landing. */
  attribution: Attribution;
  /**
   * The check this tab had before "Check a different flight" (`startNewCheck`): sent as
   * `createEnquiry.prefillContactFrom`, so the new check's S7 is pre-filled. Absent in drafts
   * saved before it existed.
   */
  prefillContactFrom?: string | null;
}

export const EMPTY_DRAFT: TripDraft = {
  entry: null,
  origin: null,
  destination: null,
  connections: [],
  date: null,
  attribution: {},
  prefillContactFrom: null,
};

export const tripDraft = slot<TripDraft>('db_trip_draft', 'session', EMPTY_DRAFT);

export function updateTripDraft(patch: Partial<TripDraft>) {
  tripDraft.set({ ...tripDraft.get(), ...patch });
}

/* ── How the visitor got here ────────────────────────────── */

/**
 * The resolver's context for this tab. `first_pass` walking straight through; `returning`
 * after the banner, an email link or a row on S12 (each step then goes back to S12).
 * `target` is what the link or the S12 row was for.
 */
export interface NavContext {
  visit: Visit;
  target: LinkTarget | null;
  /** Set when a fellow passenger's own link opened this tab (S15 only). */
  passenger: number | null;
}

export const DEFAULT_NAV: NavContext = { visit: 'first_pass', target: null, passenger: null };
export const navContext = slot<NavContext>('db_nav', 'session', DEFAULT_NAV);

export function setNavContext(patch: Partial<NavContext>) {
  navContext.set({ ...navContext.get(), ...patch });
}

/* ── S14-expired ─────────────────────────────────────────── */

/** The expired (or revoked) link that brought them, for S14-expired's "Email me a new link". */
export interface ExpiredLinkState {
  token: string;
  details: ExpiredLink;
}

export const expiredLink = slot<ExpiredLinkState | null>('db_expired_link', 'session', null);

/**
 * "Check a different flight": a new check in this tab. The old check's contact pre-fills S7:
 * the draft keeps its id for `useTrip().ensureEnquiry()` (S3), and it is returned for the
 * boarding-pass scan (S0b), which creates the enquiry itself.
 */
export function startNewCheck(): { prefillContactFrom: string | null } {
  const { attribution, entry, prefillContactFrom: earlier } = tripDraft.get();
  // Twice in a row (no enquiry in between) keeps the check before both.
  const previous = currentEnquiry.get() ?? earlier ?? null;
  currentEnquiry.clear();
  tripDraft.set({ ...EMPTY_DRAFT, attribution, entry, prefillContactFrom: previous });
  navContext.set(DEFAULT_NAV);
  return { prefillContactFrom: previous };
}

/** Everything this module keeps (dev reset, tests). */
export function clearFunnelSession() {
  for (const s of [currentEnquiry, bannerEnquiry, tripDraft, navContext, expiredLink] as Slot<unknown>[])
    s.clear();
}
